import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import { Box, Stack } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import GenericModal from 'components/GenericModal';
import MDTypography from 'components/MDTypography';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { getAlerts } from 'services/dataService';

import ControllerAlert from './ControllerAlert';

const modalWidth = 'min(760px, 92vw)';

const modalSize = {
  width: modalWidth,
  maxHeight: '80vh',
};

const ControllerAlertsModal = ({ open, onClose, controllerId, timeRange, userTimeZone }) => {
  const [controllerAlerts, setControllerAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState(null);

  const groupedAlerts = useMemo(() => {
    return controllerAlerts.reduce((acc, alert) => {
      const alertType = alert.description ?? alert.event_type;

      if (!acc[alertType]) {
        acc[alertType] = [];
      }

      acc[alertType].push({
        start: alert.start,
        end: alert.end,
        start_unix: alert.start_unix,
        end_unix: alert.end_unix,
        color: alert.color,
        description: alert.description,
      });

      return acc;
    }, {});
  }, [controllerAlerts]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isCurrent = true;

    const fetchControllerAlerts = async () => {
      setAlertsLoading(true);
      setAlertsError(null);

      try {
        const alerts = await getAlerts(controllerId, timeRange, userTimeZone, 'controller');
        if (isCurrent) {
          setControllerAlerts(Array.isArray(alerts) ? alerts : []);
        }
      } catch (error) {
        console.error('Error fetching controller alerts:', error);
        if (isCurrent) {
          setAlertsError('Failed to load alerts.');
          setControllerAlerts([]);
        }
      } finally {
        if (isCurrent) {
          setAlertsLoading(false);
        }
      }
    };

    fetchControllerAlerts();

    return () => {
      isCurrent = false;
    };
  }, [controllerId, open, timeRange, userTimeZone]);

  const ControllerAlertsContent = () => {
    if (alertsLoading) {
      return (
        <Box sx={{ width: modalWidth, py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (alertsError) {
      return (
        <Box sx={{ width: modalWidth, p: 3 }}>
          <MDTypography variant="body2" color="error" textAlign="center">
            {alertsError}
          </MDTypography>
        </Box>
      );
    }

    if (!controllerAlerts.length) {
      return (
        <Box sx={{ width: modalWidth, p: 3 }}>
          <MDTypography variant="body2" color="text" textAlign="center">
            No alerts for the selected time range.
          </MDTypography>
        </Box>
      );
    }

    return (
      <Stack spacing={1} sx={{ width: modalWidth, maxHeight: '70vh', overflowY: 'auto', p: 2 }}>
        {Object.entries(groupedAlerts).map(([alertType, alertTimes]) => (
          <ControllerAlert
            key={alertType}
            description={alertType}
            sensorId={controllerId}
            showSensorId={false}
            alertsNum={alertTimes.length}
            alertTimes={alertTimes}
          />
        ))}
      </Stack>
    );
  };

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      title="Controller alerts"
      Component={ControllerAlertsContent}
      size={modalSize}
      closeOnBackdrop={true}
      closeOnEsc={true}
      titleIcon={<NotificationImportantIcon sx={{ scale: '1.4', marginLeft: '.5rem' }} />}
    />
  );
};

ControllerAlertsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  controllerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  timeRange: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  userTimeZone: PropTypes.string.isRequired,
};

export default ControllerAlertsModal;
